import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import questionService from '@/api/question'
import { QUESTION_STATUS } from '@/constants/marketBackendEnums'
import useI18n from '@/hooks/useI18n'
import { questionDetailUrl, keepQueryString } from '@/urls'
import CryptoAssetBadge from '@/features/market/crypto-asset-badge'
import OverlayPanel from '@/components/OverlayPanel/OverlayPanel'
import { useCryptoCurrentPrice } from '@/features/market/crypto-price-stats'
import {
  formatCryptoPrice,
  getCryptoAssetName,
  getCryptoWindowPrefix,
  getPriceToBeat,
  isCryptoMarketSettled,
} from '@/features/market/utils/cryptoEvent'

const SECOND = 1000
const MINUTE = 60 * SECOND
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

const pad = value => String(value).padStart(2, '0')

/** Time left with its units spelled out; urgency colours as the market page countdown. */
const CompactCountdown = ({ endDate, pendingLabel, closesInLabel }) => {
  const endTS = Date.parse(endDate ?? '')
  const [remaining, setRemaining] = useState(null)

  useEffect(() => {
    if (!Number.isFinite(endTS)) return undefined
    const tick = () => setRemaining(endTS - Date.now())
    tick()
    const timer = setInterval(tick, SECOND)
    return () => clearInterval(timer)
  }, [endTS])

  // The row keeps its height before the client fills the digits, so the tile does not move.
  if (remaining === null) return <span className="block h-4" aria-hidden />
  // Past the end the market waits on the backend's resolution: say so instead of 0m 00s.
  if (remaining <= 0) return <span className="text-xs font-semibold text-Neutral-4">{pendingLabel}</span>
  const left = remaining
  const tone = left < MINUTE ? 'text-negative-5' : left <= 5 * MINUTE ? 'text-warning-5' : 'text-Neutral-6'
  // Units spelled out: "2h 01m" and "2m 01s" must not read the same.
  const text =
    left >= DAY
      ? `${Math.floor(left / DAY)}d ${pad(Math.floor((left % DAY) / HOUR))}h`
      : left >= HOUR
        ? `${Math.floor(left / HOUR)}h ${pad(Math.floor((left % HOUR) / MINUTE))}m`
        : `${Math.floor(left / MINUTE)}m ${pad(Math.floor((left % MINUTE) / SECOND))}s`

  return (
    <span
      role="timer"
      aria-label={`${closesInLabel} ${text}`}
      className={`text-sm font-normal leading-4 tabular-nums ${tone}`}>
      {text}
    </span>
  )
}

/**
 * Feed card header for crypto price up/down markets: "Hourly: Bitcoin up or down?" with the time
 * left at the other end of the row, then a panel with the price to beat on one side, the live
 * price on the other, coloured by its side of the target, and the asset tile in the middle.
 * Fills the header slot the card gives it (see MarketCard); `leading` sits before the title.
 */
const CryptoPriceQuestionHeader = ({ question, leading = null }) => {
  const i18n = useI18n()
  const target = getPriceToBeat(question)
  const isResolved = question?.status === QUESTION_STATUS.RESOLVED
  const isCancelled = questionService.isCancelled(question)
  const isPending = questionService.isWaitingForResults(question)
  const isOpen = !isCryptoMarketSettled(question)
  const pendingLabel = i18n('question_card.crypto_card_pending')
  const current = useCryptoCurrentPrice(question)

  const diff = current === null ? null : current - target
  const above = diff !== null && diff >= 0
  const valueTone = diff === null ? 'text-Neutral-6' : above ? 'text-positive-4' : 'text-negative-4'
  const directionLabel = diff === null ? '' : i18n(above ? 'question_card.crypto_card_above' : 'question_card.crypto_card_below')
  const currentLabel = isOpen ? i18n('hardcoded.current') : i18n('question_card.crypto_card_final')

  const windowPrefix = getCryptoWindowPrefix(question)

  const renderStatus = () => {
    const label = isResolved
      ? i18n('question_card.crypto_card_resolved')
      : isCancelled
        ? i18n('question_card.crypto_card_cancelled')
        : isPending
          ? pendingLabel
          : null
    if (label) return <span className="text-xs font-semibold text-Neutral-4">{label}</span>
    return (
      <CompactCountdown
        endDate={question?.event_end_date || question?.bet_end_date}
        pendingLabel={pendingLabel}
        closesInLabel={i18n('question_card.crypto_card_closes_in')}
      />
    )
  }

  return (
    <header className="w-full min-h-[98px] flex flex-col justify-center gap-2">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          {leading}
          <Link to={keepQueryString(questionDetailUrl(question))} className="min-w-0" title={question?.title}>
            <h3 className="question-card__title truncate text-sm font-semibold leading-[18px] text-Neutral-6 hover:underline">
              {windowPrefix ? `${windowPrefix}: ` : ''}
              {i18n('question_card.crypto_card_title', { asset: getCryptoAssetName(question) })}
            </h3>
          </Link>
        </div>
        <div className="shrink-0 leading-4">{renderStatus()}</div>
      </div>

      {/* Neutral-2 is the card's own colour in dark (see AGENTS.md), so the panel steps up to
          Neutral-3 there; the app theme is `data-theme` on <html>, not the OS preference. */}
      <div className="flex items-center justify-between gap-2 rounded-lg bg-Neutral-2 [html[data-theme='dark']_&]:bg-Neutral-3 px-3 py-2.5">
        {/* The whole block, label and figure, is the hover target of the "to beat" explanation:
            the app tooltip opens at once, where the browser's own title waits a second. */}
        <OverlayPanel
          containerClassName="!flex min-w-0 flex-1 basis-0"
          customPosition={{ top: '100%', left: 0 }}
          className="border border-solid border-Neutral-3 bg-surface-2 text-Neutral-6 text-xs font-normal !w-[220px] !p-3"
          showDelay={0}
          hideDelay={200}
          content={i18n('question_card.crypto_card_to_beat_help')}>
          <div className="flex min-w-0 flex-col">
            <span className="w-fit text-xs leading-4 text-Neutral-4 underline decoration-dotted underline-offset-2">
              {i18n('hardcoded.to_beat')}
            </span>
            <span className="truncate text-base font-bold leading-5 tabular-nums text-Neutral-6">
              {formatCryptoPrice(target)}
            </span>
            {/* Matches the difference line under the live price, so both columns share a baseline. */}
            <span className="block h-4" aria-hidden />
            <span className="sr-only">{i18n('question_card.crypto_card_to_beat_help')}</span>
          </div>
        </OverlayPanel>

        {/* Reached through the card's own click, like the rest of its body. */}
        <div className="flex shrink-0 items-center px-2">
          <CryptoAssetBadge question={question} className="w-10 h-10" />
        </div>

        <div className="flex min-w-0 flex-1 basis-0 flex-col items-end text-right">
          <span className="sr-only">
            {current === null ? currentLabel : `${currentLabel} ${formatCryptoPrice(current)} ${directionLabel}`}
          </span>
          <span className="text-xs leading-4 text-Neutral-4" aria-hidden>
            {currentLabel}
          </span>
          {/* Keyed on the value: each new price remounts the span and replays the settle-in. */}
          <span
            key={current ?? 'none'}
            className={`block truncate max-w-full text-base font-bold leading-5 tabular-nums animate-price-tick motion-reduce:animate-none ${valueTone}`}
            aria-hidden>
            {current === null ? '—' : formatCryptoPrice(current)}
          </span>
          {diff !== null && (
            <span className={`text-xs leading-4 tabular-nums ${valueTone}`} aria-hidden>
              {above ? '+' : '-'}
              {formatCryptoPrice(Math.abs(diff))}
            </span>
          )}
        </div>
      </div>
    </header>
  )
}

export default CryptoPriceQuestionHeader
